// ABOUTME: Named, finite color tokens for values that don't fit an MUI palette slot
// ABOUTME: Companion to theme.ts — see docs/CSS_CONVENTIONS.md for when to use which

// Star/celebration accent colors (distinct from `success`, which theme.ts documents as
// the "Star" behavior type's slot in comment only — in practice the app renders stars/
// celebrations in gold/amber, never in `success`'s green, so these are their own family).
export const celebrationGold = '#FFD700';
export const celebrationGoldHover = '#FFED4E';
export const starAccent = '#FFC107';

// nalu-v1's "Goals to celebrate" banner wash on the child kiosk dashboard (Keiki Mode
// mockup) — a lighter peach tint than the secondary palette's coral, used nowhere else.
export const naluV1CelebrationBannerBg = '#F1A98C';
// Muted brown subtitle text against the banner's peach wash — darker than theme.palette.text.secondary
// for contrast, per the same mockup.
export const naluV1CelebrationBannerText = '#7C5740';

// nalu-v1's caption text under the Star/Triangle/Storm tally icons (Keiki Dashboard mockup)
// — same muted brown-gray already used inline for MuiTab's unselected label in theme.nalu-v1.ts.
export const naluV1TallyLabelText = '#6E6258';

// Reused neutral gray scale (Tailwind's default gray ramp — the app already uses these
// exact values pervasively; naming them Tailwind-style keeps the numbering recognizable).
export const neutralGray = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
} as const;

// Reused light/extreme error tints not covered by theme.palette.error's main/light/dark
// (light/dark map to #F87171/#DC2626 already; these are the paler background/darkest text
// variants Tailwind's red scale provides that the app also uses).
export const errorTint = {
  50: '#FEF2F2',
  200: '#FECACA',
  800: '#B91C1C',
} as const;

// MUI-scale light tint backgrounds paired with success/warning/error text or borders
// (distinct from errorTint above, which is Tailwind's red scale — these are MUI's own
// green 50/orange 50/red 50 values, used as status-callout backgrounds).
export const statusTint = {
  successBg: '#E8F5E9',
  warningBg: '#FFF3E0',
  errorBg: '#FFEBEE',
  infoBg: '#EFF6FF',
} as const;

// Decorative gradients used more than once with no brand/semantic meaning of their own.
export const decorativeGradients = {
  rewardCard: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  heroPurple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  lightCard: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  dangerCard: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
  softBlue: 'linear-gradient(to bottom right, #eff6ff, #faf5ff)',
} as const;

// Third-party brand colors (Google, Facebook, Amazon Alexa). These are NOT BePono brand
// colors — they're fixed identities owned by other companies (Google's G logo, Facebook's
// blue, Alexa's cyan) and must render exactly as those companies specify, regardless of our
// own theme. Never converge these to our palette.
export const oauthBrandColors = {
  google: '#4285F4',
  googleGreen: '#34A853',
  googleYellow: '#FBBC05',
  googleRed: '#EA4335',
  facebook: '#1877F2',
} as const;
export const alexaBrandColor = '#00CAFF';

// Confetti burst palettes — purely decorative, categorical-by-particle, no brand meaning.
export const confettiColors = ['#FFD700', '#FFA500', '#FF6347', '#9370DB', '#00CED1'] as const;
export const confettiFireColors = ['#FF4500', '#FF6000', '#FF8C00', '#FFA500', '#FFD700', '#FF0000'] as const;

// Darker companion shade for theme.palette.behaviorHeart — used for a stroke/outline variant
// where the base heart color needs contrast against itself.
export const behaviorHeartDark = '#C2185B';

// A second, distinct chart-series blue (distinguishable from theme.palette.info.main) used
// alongside it as a separate simultaneously-relevant line/metric in admin analytics charts.
export const chartAccentBlue = '#2196F3';

// Contrast text for the ladder-position pill (dark text on light/amber positions, white text
// on dark positions) — exact values preserved rather than theme.palette.text.*/common.white,
// since those resolve to rgba()/shorthand-hex strings that a test asserts exact hex against.
export const pillContrastText = {
  dark: '#333333',
  light: '#FFFFFF',
} as const;

// Ladder position colors (bottom/negative to top/positive) — a deliberate 7-step sequential
// scale distinct from the streak-intensity scale above, indexed 0 = position 1.
export const ladderPositionColors = [
  '#F44336', // 1 - Timeout (red)
  '#FF5722', // 2 - CalmAndReset (deep orange)
  '#FF9800', // 3 - StopAndThink (orange)
  '#FFC107', // 4 - Ready (amber)
  '#8BC34A', // 5 - GoodDay (light green)
  '#4CAF50', // 6 - GreatJob (green)
  '#2E7D32', // 7 - Outstanding (dark green)
] as const;

// Ladder position colors for the avatar pill / PonoMeter (a distinct sequential scale from
// ladderPositionColors above — same idea, independently authored, slightly different values
// at positions 5 and 7 — preserved as-is rather than merged, since harmonizing them would be
// a visual change, not a consolidation).
export const ponoMeterLadderColors: Record<number, string> = {
  1: '#F44336', // red
  2: '#FF5722', // red-orange
  3: '#FF9800', // orange
  4: '#FFC107', // yellow (amber)
  5: '#CDDC39', // lime
  6: '#8BC34A', // light green
  7: '#4CAF50', // green
};

// Recording-streak fire intensity — a deliberate 7-step sequential/categorical scale, not a
// brand-slot convergence candidate (each step represents an escalating streak tier).
export const streakIntensityColors = {
  none: '#9E9E9E',
  small: '#FF9800',
  medium: '#FF5722',
  large: '#F44336',
  intense: '#D32F2F',
  blazing: '#B71C1C',
  inferno: '#7F0000',
} as const;

// Contrast text for the behavior-type buttons in index.css (dark-on-light text over each
// button's light background state).
export const behaviorButtonContrastText = {
  star: '#78350F',
  triangle: '#9A3412',
  square: '#7F1D1D',
} as const;
